import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";
import { getCapturedUtm, clearCapturedUtm } from "../../utils/captureUtm";

export default function CareersPage() {
  const { slug = "ample" } = useParams();
  const [page, setPage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [openJobId, setOpenJobId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Careers - Ample Hire";
    setLoading(true);
    axios
      .get(`${baseUrl}/job-posting/api/public/careers/${slug}`)
      .then((r) => {
        setPage(r.data?.page || {});
        setJobs(r.data?.jobs || []);
      })
      .catch(() => { setPage(null); setJobs([]); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-10 text-center text-slate-500">Loading careers…</div>;
  if (!page) return <div className="p-10 text-center text-slate-500">Careers page not found.</div>;

  const brand = page.brandColor || "#7B5AC4";

  return (
    <div className="min-h-screen bg-slate-50">
      <header style={{ background: brand, color: "white" }} className="py-16 px-6 text-center">
        <h1 className="text-4xl font-bold">{page.heroHeadline || page.name || "Careers"}</h1>
        <p className="mt-3 text-lg opacity-90">{page.heroSubtext}</p>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">Open positions ({jobs.length})</h2>
        {jobs.length === 0 ? (
          <div className="text-slate-500 py-8 text-center">No open positions right now. Check back soon!</div>
        ) : (
          <ul className="space-y-3">
            {jobs.map((j) => (
              <li key={j.id} className="bg-white border border-slate-200 rounded-xl">
                <button
                  className="w-full text-left p-4 flex items-center justify-between"
                  onClick={() => setOpenJobId(openJobId === j.id ? null : j.id)}
                >
                  <div>
                    <div className="font-semibold text-slate-900">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{j.hiringType} · {j.status}</div>
                  </div>
                  <span className="text-slate-400">{openJobId === j.id ? "−" : "+"}</span>
                </button>
                <div className="px-4 pb-3 -mt-1 text-right">
                  <Link
                    to={`/apply/${j.id}`}
                    className="inline-block text-xs font-medium px-3 py-1 rounded"
                    style={{ background: brand, color: "white" }}
                  >
                    Apply →
                  </Link>
                </div>
                {openJobId === j.id && <ApplyForm slug={slug} jobId={j.id} jobTitle={j.title} />}
              </li>
            ))}
          </ul>
        )}
      </main>

      <footer className="py-6 text-center text-xs text-slate-400">
        Powered by Ample Hire
      </footer>
    </div>
  );
}

function ApplyForm({ slug, jobId, jobTitle }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", linkedinUrl: "",
    coverLetter: "", consentAiScoring: false, consentDataProcessing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.firstName || !form.email) {
      setError("First name and email required.");
      return;
    }
    if (!form.consentDataProcessing) {
      setError("Please consent to data processing.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const utm = getCapturedUtm();
      const r = await axios.post(
        `${baseUrl}/job-posting/api/public/careers/${slug}/jobs/${jobId}/apply`,
        { ...form, ...utm }
      );
      setDone(r.data);
      // First successful submission consumes the captured UTM so a user who
      // applies to multiple jobs in the same tab doesn't double-attribute.
      clearCapturedUtm();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Application failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border-t border-slate-200 p-5 bg-emerald-50">
        <div className="text-emerald-700 font-medium">✓ {done.message}</div>
        <div className="text-xs text-emerald-600 mt-1">Application ID: #{done.applicationId}</div>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 p-5">
      <div className="text-sm text-slate-600 mb-4">Apply for <strong>{jobTitle}</strong></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} placeholder="First name *" className="px-3 py-2 border border-slate-300 rounded text-sm" />
        <input value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} placeholder="Last name" className="px-3 py-2 border border-slate-300 rounded text-sm" />
        <input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email *" className="px-3 py-2 border border-slate-300 rounded text-sm" />
        <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone" className="px-3 py-2 border border-slate-300 rounded text-sm" />
        <input value={form.linkedinUrl} onChange={(e) => setForm({...form, linkedinUrl: e.target.value})} placeholder="LinkedIn URL" className="md:col-span-2 px-3 py-2 border border-slate-300 rounded text-sm" />
        <textarea value={form.coverLetter} onChange={(e) => setForm({...form, coverLetter: e.target.value})} placeholder="Cover letter (optional)" className="md:col-span-2 px-3 py-2 border border-slate-300 rounded text-sm resize-none" rows={4} />
      </div>
      <div className="mt-3 space-y-1">
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.consentDataProcessing} onChange={(e) => setForm({...form, consentDataProcessing: e.target.checked})} />
          I consent to processing of my personal data (required)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.consentAiScoring} onChange={(e) => setForm({...form, consentAiScoring: e.target.checked})} />
          I consent to AI-powered evaluation of my responses
        </label>
      </div>
      {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
      <button
        onClick={submit}
        disabled={submitting}
        className="mt-4 px-4 py-2 rounded text-white text-sm font-medium disabled:opacity-50"
        style={{ background: "#7B5AC4" }}
      >
        {submitting ? "Submitting…" : "Submit application"}
      </button>
    </div>
  );
}
